// app/javascript/projects/components/ProjectsDisplay.jsx

import React, { useEffect, useState } from 'react';
import BillingModalMain from './BillingModalMain'
import BillingModalRenew from './BillingModalRenew'
import BillingModalUpgrade from './BillingModalUpgrade'
import BillingModalPauseSurvey from './BillingModalPauseSurvey';
import BillingModalPauseFeedbackDetail from './BillingModalPauseFeedbackDetail';
import BillingModalPauseHelp from './BillingModalPauseHelp';
import BillingModalPauseConfirm from './BillingModalPauseConfirm';
import BillingModalEndTrial from 'accounts/end_trial/BillingModalEndTrial';
import AccountPageConstants from 'constants/AccountPageConstants'
import GenericModal from '../GenericModal'
import { useHistory, useLocation } from 'react-router-dom';
import { useTeamModal } from '../../../hooks/subscriptions/useTeamModal';
import CancelConstants from '../cancel/CancelConstants';

/**
 * BillingModal - Handles navigation and minimal shared state for multi-step flows
 */
function BillingModal() {
  const { page, showBillingModal, closeModal, navigateToPage } = useTeamModal();
  const history = useHistory();
  const location = useLocation();

  // Shared state for pause feedback flow (only for pages that need it)
  const [pauseFeedbackChoice, setPauseFeedbackChoice] = useState(null);
  const [pauseFeedbackText, setPauseFeedbackText] = useState('');

  const gotoUserPage = () => {
    if (isOnUpgradePage())
      history.push('/users');
  };

  const onHide = () => {
    closeModal();
    gotoUserPage();
  };

  const isOnUpgradePage = () => {
    return location.pathname.includes('upgrade');
  };

  // Determine back navigation based on current page
  const getBackDestination = () => {
    switch (page) {
      case AccountPageConstants.PAUSE_FEEDBACK_SURVEY:
        return AccountPageConstants.PLAN_AND_BILLING;
      case AccountPageConstants.PAUSE_FEEDBACK_DETAIL:
        return AccountPageConstants.PAUSE_FEEDBACK_SURVEY;
      case AccountPageConstants.PAUSE_FEEDBACK_HELP:
        return AccountPageConstants.PAUSE_FEEDBACK_DETAIL;
      case AccountPageConstants.PAUSE_FEEDBACK_CONFIRM:
        // Check choice to determine back destination
        const needsHelp = pauseFeedbackChoice === CancelConstants.TECHNICAL || pauseFeedbackChoice === CancelConstants.HELP;
        return needsHelp ? AccountPageConstants.PAUSE_FEEDBACK_HELP : AccountPageConstants.PAUSE_FEEDBACK_DETAIL;
      case AccountPageConstants.UNCANCEL_SUBSCRIPTION:
      case AccountPageConstants.ANNUAL_SUBSCRIPTION:
        return AccountPageConstants.PLAN_AND_BILLING;
      default:
        return AccountPageConstants.PLAN_AND_BILLING;
    }
  };

  const backButtonPressed = () => {
    // Special handling for upgrade page - show main billing page
    if (isOnUpgradePage()) {
      navigateToPage(AccountPageConstants.PLAN_AND_BILLING);
      gotoUserPage()
      return;
    }
    
    const destination = getBackDestination();
    navigateToPage(destination);
    if (destination === AccountPageConstants.PLAN_AND_BILLING) {
      gotoUserPage();
    }
  };

  // Reset pause feedback state when leaving the flow
  useEffect(() => {
    const isInPauseFeedbackFlow = [
      AccountPageConstants.PAUSE_FEEDBACK_SURVEY,
      AccountPageConstants.PAUSE_FEEDBACK_DETAIL,
      AccountPageConstants.PAUSE_FEEDBACK_HELP,
      AccountPageConstants.PAUSE_FEEDBACK_CONFIRM
    ].includes(page);
    
    if (!isInPauseFeedbackFlow) {
      setPauseFeedbackChoice(null);
      setPauseFeedbackText('');
    }
  }, [page]);

  // Render the appropriate page component based on current page
  const renderPage = () => {
    // Override for upgrade page - always show BillingModalEndTrial
    if (isOnUpgradePage()) {
      return <BillingModalEndTrial />;
    }
    
    switch (page) {
      case AccountPageConstants.PLAN_AND_BILLING:
        return <BillingModalMain />;
      
      // Pause feedback flow pages - with shared state
      case AccountPageConstants.PAUSE_FEEDBACK_SURVEY:
        return <BillingModalPauseSurvey 
          choice={pauseFeedbackChoice}
          onChoiceChange={setPauseFeedbackChoice}
        />;
      case AccountPageConstants.PAUSE_FEEDBACK_DETAIL:
        return <BillingModalPauseFeedbackDetail 
          choice={pauseFeedbackChoice}
          feedback={pauseFeedbackText}
          onFeedbackChange={setPauseFeedbackText}
        />;
      case AccountPageConstants.PAUSE_FEEDBACK_HELP:
        return <BillingModalPauseHelp />;
      case AccountPageConstants.PAUSE_FEEDBACK_CONFIRM:
        return <BillingModalPauseConfirm />;
      
      // Other billing pages
      case AccountPageConstants.UNCANCEL_SUBSCRIPTION:
        return <BillingModalRenew onSuccess={() => navigateToPage(AccountPageConstants.PLAN_AND_BILLING)} />;
      case AccountPageConstants.ANNUAL_SUBSCRIPTION:
        return <BillingModalUpgrade />;
      case AccountPageConstants.END_TRIAL:
        return <BillingModalEndTrial />;
      
      default:
        return <div />;
    }
  };

  // Show back button if not on main billing page OR if on upgrade page
  const shouldShowBackButton = page !== AccountPageConstants.PLAN_AND_BILLING || isOnUpgradePage();

  return (
    <GenericModal
      show={showBillingModal || isOnUpgradePage()}
      onHide={onHide}
      backButtonClick={shouldShowBackButton ? backButtonPressed : null}
    >
      {renderPage()}
    </GenericModal>
  );
}

export default BillingModal;